export type DocType = 'invoice' | 'order_response' | 'despatch_advice' | 'credit_note'

export interface TdLine {
  article_number: string
  gtin: string
  description: string
  quantity: number
  unit: string
  unit_price: number
  vat_percentage: number
  vat_code: string
  // REQ-TESTDATA-004 (v0.8.1) — intended decimal scale for a materialized
  // precision-test line (see `LineGenerationConfig` below). `null`/absent for
  // ordinary catalogue-mode lines — completely unused/unchanged there.
  quantity_scale?: number | null
  unit_price_scale?: number | null
  // REQ-TESTDATA-005/007 (CAP-003 v1.0/v1.2) — set only by the backend
  // financial-scenario resolver (`financial_scenarios.scenario_to_testdata_lines`),
  // round-tripped by the frontend unmodified. `net_amount_override` (and the
  // other scenario fields) present means this line is an already-materialized
  // canonical scenario line — the presence of `net_amount_override` is the
  // exact "already materialized" marker the backend's idempotency contract
  // relies on (REQ-TESTDATA-007 "Canonical materialization contract"). The
  // frontend never sets or computes these itself.
  vat_category?: string | null
  net_amount_override?: string | null
  gross_line_amount?: string | null
  line_allowance_amount?: string | null
  line_charge_amount?: string | null
  document_allowance_share?: string | null
  document_charge_share?: string | null
  vat_amount_override?: string | null
  // REQ-TESTDATA-007 (CAP-003 v1.2) — explicit provenance: the identity
  // (`article_number`) that WAS this line's base the last time a financial
  // scenario resolved it, set only by the backend and round-tripped by the
  // frontend unmodified (never computed/guessed client-side). Lets a later
  // scenario resolution borrow this line's identity idempotently — even
  // across a Scenario -> Normal -> Scenario round-trip or an A -> B switch
  // — without ever corrupting a genuine source article number that happens
  // to already end in a string a scenario also uses as a suffix (e.g. a
  // real article legitimately named "PUMP-TRANSPORT"). `null`/absent means
  // this line has never been through scenario materialization.
  scenario_identity_base?: string | null
}

// REQ-TESTDATA-007 (CAP-003 v1.2) — Financial Scenario Preview. Mirrors
// `backend/app/services/testdata/schemas.py`'s `FinancialScenarioPreviewRequest`/
// `FinancialScenarioPreviewResponse` and the Decimal-safe machine payload
// shape already used for Ground Truth's `financialScenario` block
// (`test_reference._financial_scenario_dict`) — every monetary value is a
// string, never a float, per REQ-TESTDATA-005 §20/§22.
export interface FinancialScenarioTaxBreakdownRow {
  category: string
  rate: string
  taxableBase: string
  taxAmount: string
  grossContribution: string
}

export interface FinancialScenarioDocumentAllowance {
  amount: string
  targetVatCategory: string
  targetVatRate: string
  taxableBaseBefore: string
  taxableBaseAfter: string
}

export interface FinancialScenarioTotals {
  lineExtensionTotal: string
  allowanceTotal: string
  chargeTotal: string
  taxExclusiveTotal: string
  taxTotal: string
  taxInclusiveTotal: string
  prepaidAmount: string
  payableAmount: string
  roundingAmount: string
}

export interface FinancialScenarioReconciliationCheck {
  check: string
  left: string
  right: string
  result: boolean
}

export interface FinancialScenarioPayload {
  scenarioId: string
  version: string
  lines: unknown[]
  taxBreakdown: FinancialScenarioTaxBreakdownRow[]
  documentAllowanceAllocation: unknown[]
  documentAllowance: FinancialScenarioDocumentAllowance | null
  totals: FinancialScenarioTotals
  reconciliationChecks: FinancialScenarioReconciliationCheck[]
}

export interface FinancialScenarioPreviewResponse {
  lines: TdLine[]
  scenario: FinancialScenarioPayload
  // REQ-TESTDATA-007 (CAP-003 v1.2) — review fix: the canonical
  // materialization is not just `lines`. These document-level totals
  // (`document_allowance_share` is only a per-line pro-rata SHARE, not the
  // document-level total; `prepaid_amount` is not a line property at all)
  // MUST be carried forward and submitted verbatim with the Generate
  // request, or the backend's idempotency short-circuit
  // (`financial_scenario_result_from_testdata_lines`) treats them as
  // zero/absent, diverging FIN-DOC-DISCOUNT-001/FIN-PAYMENT-001/
  // FIN-COMBINED-001's totals from what preview showed.
  document_allowance_amount: number | null
  document_charge_amount: number | null
  prepaid_amount: number | null
}

// REQ-TESTDATA-003 (v0.8) — optional opt-in precision-test line generation.
// Absent/`mode: 'catalogue'` (the default) leaves normal catalogue/price-list
// generation completely unchanged; only `mode: 'precision_test'` activates
// bounded, deterministic line-count/quantity/unit-price precision generation.
export type QuantityMode = 'whole' | 'decimal'

export interface LineGenerationConfig {
  mode: 'catalogue' | 'precision_test'
  line_count: number | null
  quantity_mode: QuantityMode
  quantity_precision_min: number
  quantity_precision_max: number
  unit_price_precision_min: number
  unit_price_precision_max: number
  vary_precision_per_line: boolean
}

export const PRECISION_LINE_COUNT_MIN = 1
export const PRECISION_LINE_COUNT_MAX = 50
export const PRECISION_QUANTITY_DIGITS_MIN = 0
export const PRECISION_QUANTITY_DIGITS_MAX = 6
export const PRECISION_UNIT_PRICE_DIGITS_MIN = 2
export const PRECISION_UNIT_PRICE_DIGITS_MAX = 6

export interface PriceListSummary {
  id: number
  name: string
  supplier_gln: string
  buyer_gln: string
  row_count: number
}

export interface Template {
  id: number
  name: string
  document_type: DocType
  supplier_name: string | null
  supplier_gln: string | null
  supplier_iban: string | null
  supplier_vat: string | null
  supplier_kvk: string | null
  supplier_attn: string | null
  supplier_address: string | null
  supplier_postal: string | null
  supplier_city: string | null
  supplier_phone: string | null
  supplier_email: string | null
  supplier_website: string | null
  buyer_name: string | null
  buyer_gln: string | null
  buyer_vat: string | null
  buyer_attn: string | null
  buyer_address: string | null
  buyer_postal: string | null
  buyer_city: string | null
  delivery_party_name: string | null
  delivery_party_gln: string | null
  default_lines: TdLine[] | null
}

export type InjectionType =
  | 'price_deviation'
  | 'missing_invoice_line'
  | 'unknown_article'
  | 'partial_delivery'
  | 'overbilling'
  | 'wrong_vat_rate'
  | 'wrong_gln'
  | 'duplicate_invoice_number'

export interface InjectionSpec {
  injection_type: InjectionType
  line_index: number
  price_delta_pct: number
  qty_delta: number
  vat_rate: number | null
  wrong_gln: string | null
}

export interface VerificationOutcome {
  injection_type: InjectionType
  article_number: string | null
  expected_status: string
  actual_status: string | null
  detected: boolean
  description: string
}

export interface SetVerification {
  all_detected: boolean
  overall_status: string
  outcomes: VerificationOutcome[]
  unexpected_findings: Array<{ article_number: string; status: string; details: string }>
}

export interface SetGenerationResult {
  set_id: string
  documents: Record<string, string>
  injections: Array<Record<string, unknown>>
  verification: SetVerification
  zip_base64: string
  zip_filename: string
}

export interface DebitCreditSetResult {
  set_id: string
  documents: Record<string, string>
  zip_base64: string
  zip_filename: string
}

// Volledige testdata-generator configuratie-snapshot ("testscenario") — los van
// Template hierboven, dat alleen partij-/regelgegevens per documenttype bewaart.
export type ConfigPresetMode = 'single' | 'set' | 'debit_credit_set'

export interface ConfigPreset {
  id: number
  name: string
  mode: ConfigPresetMode
  config_json: Record<string, unknown>
}

export interface FormState {
  country: string
  document_number: string
  document_date: string
  project_number: string
  buyers_order_number: string
  suppliers_order_number: string
  order_response_number: string
  desadv_number: string
  end_customer_order_number: string
  supplier_name: string
  supplier_gln: string
  supplier_iban: string
  supplier_vat: string
  supplier_kvk: string
  // SIREN (9 cijfers) — alleen relevant bij het Franse xml_profile (cius_fr/
  // extended_ctc_fr), voor BR-FR-10/BT-30. Los van supplier_kvk: UBL staat maar
  // één CompanyID toe binnen PartyLegalEntity.
  supplier_siren: string
  supplier_attn: string
  supplier_address: string
  supplier_postal: string
  supplier_city: string
  supplier_phone: string
  supplier_email: string
  supplier_website: string
  buyer_name: string
  buyer_gln: string
  buyer_vat: string
  buyer_attn: string
  buyer_address: string
  buyer_postal: string
  buyer_city: string
  invoicer_name: string
  invoicer_gln: string
  invoicee_name: string
  invoicee_gln: string
  delivery_party_name: string
  delivery_party_gln: string
  ship_from_name: string
  ship_from_gln: string
}
